"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { PhoneCall, ArrowRight, Clock, CircleQuestionMark as HelpCircle, Wrench, Flame, Droplets, Waves, MapPin, FileText } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const faqItems = [{
  id: "item-1",
  question: "What general commercial and residential plumbing services do you offer?",
  answer: "We provide comprehensive emergency and scheduled plumbing services throughout Jonesboro. This includes high-pressure hydro jetting, fixture repairs, water line installations, backflow prevention testing, repiping, and complete commercial plumbing facility inspections.",
  icon: Wrench
}, {
  id: "item-2",
  question: "How quickly can you repair or replace a broken water heater?",
  answer: "Our mobile plumbing units carry standard diagnostic equipment and common replacement parts for gas, electric, and tankless commercial and residential water heaters. In emergency scenarios, we dispatch immediately and can perform on-the-spot repairs or same-day system replacements.",
  icon: Flame
}, {
  id: "item-3",
  question: "What methods do you use for severe drain cleaning and clogs?",
  answer: "We utilize advanced video pipe inspection cameras to pinpoint the obstruction, followed by heavy-duty mechanical augers or high-pressure hydro-jetting to completely scour grease, scale, and foreign debris from commercial and residential drainage lines.",
  icon: Waves
}, {
  id: "item-4",
  question: "What is your procedure for locating and repairing leaking pipes?",
  answer: "We utilize non-invasive acoustic sensors, thermal imaging, and pressure testing equipment to pinpoint hidden slab leaks and interior pipe fractures without causing unnecessary structural damage. Once isolated, we perform targeted repairs or pipe reroutes.",
  icon: Droplets
}, {
  id: "item-5",
  question: "Do you handle main sewer line backups and complete replacements?",
  answer: "Yes. From heavy tree root intrusion to collapsed lines, our plumbers offer both traditional excavation and trenchless sewer pipe lining solutions to restore optimal sewer line functionality with minimal disruption to your daily operations.",
  icon: Wrench
}, {
  id: "item-6",
  question: "Is emergency plumbing support available 24/7 in Jonesboro?",
  answer: "Yes, our rapid-response emergency line operates 24 hours a day, 7 days a week, 365 days a year. When you call (470) 847-9076, you speak directly with a live team member who immediately coordinates technician dispatch.",
  icon: Clock
}, {
  id: "item-7",
  question: "What service coverage areas do you support across Georgia?",
  answer: "We primarily serve Jonesboro, GA, and extend our rapid service coverage to surrounding communities across Clayton County and the greater South Atlanta metropolitan corridor.",
  icon: MapPin
}, {
  id: "item-8",
  question: "How do I request emergency service or book a site estimate?",
  answer: "For immediate emergency plumbing assistance, dial (470) 847-9076 right away. For non-urgent diagnostics, commercial maintenance quotes, or planned installations, you can submit an online service request through our contact page.",
  icon: FileText
}];
export default function CommercialFaq() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 15
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4
      }
    }
  };
  return <section id="commercialfaq" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-t border-border overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-80px"
      }} variants={containerVariants} className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <motion.div variants={itemVariants} className="inline-flex items-center justify-center mb-4">
            <Badge variant="secondary" className="px-3.5 py-1 rounded-full text-xs font-semibold gap-2 border border-border shadow-xs">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-accent"></span>
              </span>
              24/7 Urgent Response Guidance
            </Badge>
          </motion.div>

          <motion.h2 variants={itemVariants} className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
            Frequently Asked Questions
          </motion.h2>

          <motion.p variants={itemVariants} className="mt-4 text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Find prompt, direct answers regarding emergency interventions, commercial sewer solutions, water heaters, and priority dispatch across Jonesboro.
          </motion.p>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-40px"
      }} variants={containerVariants} className="mb-14">
          <Accordion type="single" collapsible className="w-full space-y-3">
            {faqItems.map((item, i) => {
            const Icon = item.icon;
            return <div key={item.id} data-index={i}>
                  <motion.div variants={itemVariants}>
                    <AccordionItem value={item.id} className="bg-card text-card-foreground border border-border rounded-xl px-4 sm:px-6 shadow-xs transition-all duration-200 hover:border-primary/40 data-[state=open]:border-primary/60 data-[state=open]:shadow-sm">
                      <AccordionTrigger className="py-5 hover:no-underline text-left text-base sm:text-lg font-semibold text-foreground group">
                        <span className="flex items-start sm:items-center gap-3 pr-2">
                          <span className="flex items-center justify-center size-8 rounded-lg bg-secondary text-primary shrink-0 mt-0.5 sm:mt-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                            <Icon className="size-4" />
                          </span>
                          <span>{item.question}</span>
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="pt-1 pb-5 text-muted-foreground text-sm sm:text-base leading-relaxed pl-11">
                        {item.answer}
                      </AccordionContent>
                    </AccordionItem>
                  </motion.div>
                </div>;
          })}
          </Accordion>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-20px"
      }} variants={containerVariants} className="bg-card text-card-foreground border border-border rounded-2xl p-6 sm:p-8 md:p-10 shadow-md text-center max-w-3xl mx-auto">
          <div className="inline-flex p-3 rounded-full bg-secondary text-primary mb-4">
            <HelpCircle className="size-6" />
          </div>
          <h3 className="font-default text-xl sm:text-2xl font-bold text-foreground">
            Have an Immediate Plumbing Emergency?
          </h3>
          <p className="mt-2 text-sm sm:text-base text-muted-foreground max-w-xl mx-auto">
            Our master technicians are on standby around the clock. Call us for instant phone triage or submit your service request online.
          </p>

          <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl px-7 font-semibold shadow-sm">
              <a href="tel:4708479076">
                <PhoneCall className="size-4 mr-2" />
                Call (470) 847-9076
              </a>
            </Button>

            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto rounded-xl px-6 font-semibold">
              <Link to="/contact/">
                Request Service
                <ArrowRight className="size-4 ml-2" />
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}